<script setup>
/**
 * @fileoverview Original Ceramic glaze picker. Selecting a glaze arms the
 * paint bucket; selecting it again puts the bucket down.
 *
 * Two views of the same (name-filtered) palette: a swatch grid, and a
 * color wheel with a magnifying lens (ColorWheel).
 *
 * @component
 */
import { ref, computed } from 'vue';
import { useMosaicStore } from '../stores/mosaicStore';
import { trackColorSelection } from '../utils/trackingService';
import ColorOptionGrid from './Shared/ColorOptionGrid.vue';
import ColorWheel from './ColorWheel.vue';

const store = useMosaicStore();
const filter = ref('');
/** 'swatches' | 'wheel' */
const view = ref('swatches');
const views = [
    { key: 'swatches', label: 'Swatches' },
    { key: 'wheel', label: 'Color Wheel' },
];

const filteredColors = computed(() => {
    const q = filter.value.trim().toLowerCase();
    if (!q) return store.glazeColors;
    const out = {};
    for (const [name, hex] of Object.entries(store.glazeColors)) {
        if (name.toLowerCase().includes(q)) out[name] = hex;
    }
    return out;
});

const selectedNames = computed(() => (store.activeColorName ? [store.activeColorName] : []));

function onSelect(name) {
    if (store.activeColorName === name) {
        store.setActiveColor(null);
        return;
    }
    store.setActiveColor(name);
    trackColorSelection(name, store.glazeColors[name], view.value);
}
</script>

<template>
    <div class="flex flex-col gap-3">
        <input
            v-model="filter"
            type="search"
            placeholder="Filter colors by name..."
            aria-label="Filter colors by name"
            class="w-full border border-engobe px-3 py-2 text-sm"
        />
        <div class="flex gap-2" role="group" aria-label="Color picker view">
            <button
                v-for="v in views"
                :key="v.key"
                type="button"
                class="px-3 py-1 text-xs border rounded-full transition"
                :class="
                    view === v.key
                        ? 'bg-dryline text-white border-dryline'
                        : 'bg-white text-dryline border-engobe hover:border-dryline'
                "
                :aria-pressed="view === v.key ? 'true' : 'false'"
                @click="view = v.key"
            >
                {{ v.label }}
            </button>
        </div>
        <ColorWheel
            v-if="view === 'wheel'"
            :colors="filteredColors"
            :selected-name="store.activeColorName"
            @select="onSelect"
        />
        <div v-else class="p-2 md:p-3 bg-gray5new max-h-[36vh] overflow-auto">
            <ColorOptionGrid
                :colors="filteredColors"
                :images="store.glazeColorImages"
                :records="store.glazeColorRecords"
                :selected-names="selectedNames"
                :max-selectable="1"
                aria-label="Color options"
                zoom-preview
                @select="onSelect"
            />
            <p
                v-if="Object.keys(filteredColors).length === 0"
                class="text-sm text-dryline text-center py-4 mb-0"
            >
                No colors match “{{ filter }}”.
            </p>
        </div>
        <p
            v-if="view === 'wheel' && Object.keys(filteredColors).length === 0"
            class="text-sm text-dryline text-center mb-0"
        >
            No colors match “{{ filter }}”.
        </p>
    </div>
</template>
